import {
  Popover,
  PopoverCollision,
  PopoverPosition,
} from "@progress/kendo-react-tooltip";
import React from "react";

import "./tooltipconent.scss";

const CLOSE_DELAY_MS = 120;
const KEY_SCROLL_STEP_PX = 40;

// Keyboard users can't reach the portal-rendered list by Tab, so these keys on
// the focused trigger scroll it instead (WCAG 2.1.1).
const SCROLL_KEYS: Record<string, (el: HTMLElement) => number> = {
  ArrowDown: el => el.scrollTop + KEY_SCROLL_STEP_PX,
  ArrowUp: el => el.scrollTop - KEY_SCROLL_STEP_PX,
  PageDown: el => el.scrollTop + el.clientHeight,
  PageUp: el => el.scrollTop - el.clientHeight,
  Home: () => 0,
  End: el => el.scrollHeight,
};

// Kendo's Popover defaults to `collision: { horizontal: "none", vertical: "none" }`,
// which renders the bubble at its requested position even when that puts it
// outside the viewport — a `position="right"` tooltip on a cell near the right
// edge of the window gets clipped by the browser edge and is unreadable.
// `fit` slides the bubble back into view along both axes while keeping the
// requested side (and therefore the callout arrow's direction) intact; `flip`
// would move it to the opposite side, but Kendo derives the callout class from
// the `position` prop rather than the resolved placement, so a flipped bubble
// ends up with an arrow pointing away from its anchor.
const FIT_IN_VIEWPORT: PopoverCollision = {
  horizontal: "fit",
  vertical: "fit",
};

const ToolTipContent: React.FC<
  React.PropsWithChildren & {
    Content: React.FC;
    position?: PopoverPosition;
    callout?: boolean;
  }
> = props => {
  const target = React.useRef<HTMLElement | null>(null);
  const popoverContentRef = React.useRef<HTMLDivElement | null>(null);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isOpen, setOpen] = React.useState<boolean>(false);
  const { children, Content, position = "left", callout = true } = props;

  const clearCloseTimeout = React.useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const showPopover = React.useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout]);

  const scheduleClose = React.useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleMouseLeave = React.useCallback(
    (event: React.MouseEvent) => {
      // `relatedTarget` is not always a Node: leaving toward the browser chrome
      // reports the Window, and `Node.contains(window)` throws a TypeError that
      // surfaces as an uncaught application error. Guard on the type, not just
      // on truthiness.
      const nextTarget = event.relatedTarget;
      if (
        nextTarget instanceof Node &&
        (target.current?.contains(nextTarget) ||
          popoverContentRef.current?.contains(nextTarget))
      ) {
        return;
      }
      scheduleClose();
    },
    [scheduleClose],
  );

  React.useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      const content = popoverContentRef.current;
      const scrollTo = SCROLL_KEYS[event.key];
      if (
        !isOpen ||
        !scrollTo ||
        !content ||
        content.scrollHeight <= content.clientHeight
      ) {
        return;
      }
      // Only these keys, and no stopPropagation, so ancestor handlers still run.
      event.preventDefault();
      content.scrollTop = scrollTo(content);
    },
    [isOpen],
  );

  return (
    <>
      <span
        className="target-children"
        ref={target}
        onMouseEnter={showPopover}
        onMouseLeave={handleMouseLeave}
        onFocus={showPopover}
        onBlur={scheduleClose}
        onKeyDown={handleKeyDown}
      >
        {children}
      </span>

      <Popover
        className="tlx-tooltip-popover"
        // Entrance is driven by the CSS `tlx-tooltip-in` keyframe (fade + rise);
        // keep Kendo's open near-instant so they don't fight, and give it a
        // short close so the bubble fades out smoothly on mouse-leave.
        animate={{ openDuration: 10, closeDuration: 140 }}
        show={isOpen}
        anchor={target.current}
        positionMode={"fixed"}
        position={position}
        collision={FIT_IN_VIEWPORT}
        callout={callout}
        title=""
      >
        <div
          ref={popoverContentRef}
          className="tlx-tooltip-popover__content"
          onMouseEnter={showPopover}
          onMouseLeave={handleMouseLeave}
        >
          <Content />
        </div>
      </Popover>
    </>
  );
};

export default ToolTipContent;
